import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Plus, Eye, Edit, Trash2, Building, Users, FolderOpen } from 'lucide-react';
import { clientsData } from '../../data/mockData';

// Données importées depuis mockData
const clientsList = clientsData;

const ClientsListing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('raisonSociale');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);

  // Filtrage et tri des clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = clientsData.filter(client => {
      const matchesSearch = client.raisonSociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.secteur.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.adresse.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSector = !sectorFilter || client.secteur === sectorFilter;
      const matchesStatus = !statusFilter || client.statut === statusFilter;
      
      return matchesSearch && matchesSector && matchesStatus;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, sectorFilter, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const uniqueSectors = [...new Set(clientsData.map(client => client.secteur))];

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-port-gray-900">Gestion des Clients</h1>
          <p className="text-port-gray-600 mt-1">
            {filteredAndSortedClients.length} client{filteredAndSortedClients.length > 1 ? 's' : ''} trouvé{filteredAndSortedClients.length > 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/admin/clients/1" className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Client
        </Link>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-port-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, secteur, adresse..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            {/* Bouton filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
              {(sectorFilter || statusFilter) && (
                <span className="ml-2 bg-atl-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {(sectorFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filtres étendus */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-port-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Secteur</label>
                  <select
                    value={sectorFilter}
                    onChange={(e) => setSectorFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les secteurs</option>
                    {uniqueSectors.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Statut</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSectorFilter('');
                      setStatusFilter('');
                    }}
                    className="btn btn-secondary w-full"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tableau des clients */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('raisonSociale')}
                >
                  <div className="flex items-center">
                    <Building className="h-4 w-4 mr-2" />
                    Raison Sociale {getSortIcon('raisonSociale')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('secteur')}
                >
                  Secteur {getSortIcon('secteur')}
                </th>
                <th className="mobile-hidden">Contact</th>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('nombreDossiers')}
                >
                  <div className="flex items-center">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Dossiers {getSortIcon('nombreDossiers')}
                  </div>
                </th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedClients.map(client => (
                <tr key={client.id} className="hover:bg-port-gray-50">
                  <td>
                    <div>
                      <div className="font-medium text-port-gray-900">
                        {client.raisonSociale}
                      </div>
                      <div className="text-sm text-port-gray-500">
                        {client.adresse}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {client.secteur}
                    </span>
                  </td>
                  <td className="mobile-hidden">
                    <div className="text-sm">
                      <div className="flex items-center text-port-gray-900">
                        <Users className="h-4 w-4 mr-1" />
                        {client.contacts.length} contact{client.contacts.length > 1 ? 's' : ''}
                      </div>
                      <div className="text-port-gray-500">{client.telephone}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`font-medium ${
                      client.nombreDossiers > 40 ? 'text-atl-green-600' :
                      client.nombreDossiers > 20 ? 'text-yellow-600' :
                      'text-port-gray-600'
                    }`}>
                      {client.nombreDossiers}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${
                      client.statut === 'actif' ? 'badge-success' : 'badge-secondary'
                    }`}>
                      {client.statut === 'actif' ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/admin/clients/1`}
                        className="p-2 text-atl-blue-600 hover:bg-atl-blue-100 rounded-lg transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button
                        className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedClients.length === 0 && (
          <div className="text-center py-12">
            <Building className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-port-gray-900 mb-2">
              Aucun client trouvé
            </h3>
            <p className="text-port-gray-500">
              Essayez de modifier vos critères de recherche ou ajoutez un nouveau client.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsListing;
